// push.js — browser web push helpers for the operator opt-in pipe (v1).
//
// Pure + side-effecting helpers, kept thin so the React hook
// (usePushSubscription.js) owns all state. The PUBLIC VAPID key is read
// from import.meta.env.VITE_VAPID_PUBLIC_KEY — never inlined, never a
// secret (the private key lives only in the edge fn's Deno env).

// Thrown by subscribeToPush when the user denies (or dismisses) the
// browser notification permission prompt, so the hook can show a
// friendly "blocked" message instead of a raw error.
export class PushPermissionError extends Error {
  constructor(message = 'Notification permission was not granted.') {
    super(message)
    this.name = 'PushPermissionError'
    this.code = 'permission_denied'
  }
}

// Thrown by subscribeToPush when VITE_VAPID_PUBLIC_KEY is missing. We throw
// BEFORE registering the SW or requesting permission, so a missing key can
// never leave a half-configured device (registered SW + granted permission
// + no usable subscription). The hook maps this to a friendly "push isn't
// configured yet" message.
export class PushConfigError extends Error {
  constructor(message = 'Push is not configured (missing VAPID public key).') {
    super(message)
    this.name = 'PushConfigError'
    this.code = 'config_error'
  }
}

// urlBase64ToUint8Array — decode a base64url VAPID public key into the
// Uint8Array the PushManager wants as applicationServerKey. PURE: handles
// base64url (- _) and missing '=' padding. For a P-256 public key this
// returns the 65-byte uncompressed point (first byte 0x04).
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// isPushSupported — true only when the browser exposes the full push
// stack. Guards every code path so unsupported browsers (and SSR) never
// touch a missing global.
export function isPushSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  )
}

// isIos — true for iPhone / iPad / iPod user agents (incl. iPadOS 13+
// reporting as "MacIntel" with touch). Best-effort UA sniff; only used to
// decide whether to show the iOS "install first" message.
export function isIos() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const iosUa = /iphone|ipad|ipod/i.test(ua)
  const iPadOs =
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  return iosUa || iPadOs
}

// isStandalonePwa — true when running as an installed (home-screen) PWA.
// iOS exposes navigator.standalone; everywhere else we check the
// display-mode media query.
function isStandalonePwa() {
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  ) {
    return true
  }
  return false
}

// isIosNeedsInstall — true when the user is on iOS in a normal Safari tab
// (NOT the installed PWA). iOS 16.4+ only allows web push from an app
// added to the home screen, so the UI must tell them to install first.
export function isIosNeedsInstall() {
  return isIos() && !isStandalonePwa()
}

// subscribeToPush — register the SW, request permission, and create a
// PushManager subscription. Returns the subscription as plain JSON
// ({ endpoint, keys: { p256dh, auth } }) so the hook can persist it.
// LAZY SW registration lives here so a worker only lands when an operator
// opts in. Throws PushPermissionError if permission is denied.
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push is not supported in this browser.')
  }

  // Config guard FIRST: if the public VAPID key is missing we cannot create
  // a usable subscription, so bail before touching the SW or the permission
  // prompt. Otherwise a missing key leaves a registered SW + granted
  // permission with no subscription — a half-configured device.
  const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
  if (!vapidPublicKey) {
    throw new PushConfigError()
  }

  await navigator.serviceWorker.register('/sw.js')
  const reg = await navigator.serviceWorker.ready

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new PushPermissionError()
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  })

  // Normalize to the row shape the table expects. PushSubscription.toJSON()
  // yields { endpoint, keys: { p256dh, auth }, expirationTime }.
  return sub.toJSON()
}

// getSubscriptionEndpoint — read-only lookup of the current browser
// subscription's endpoint WITHOUT tearing it down. Used by the disable path
// so the hook can delete the DB row FIRST, then unsubscribe. Returns null
// when there is no active subscription.
export async function getSubscriptionEndpoint() {
  if (!isPushSupported()) return null

  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  return sub ? sub.endpoint : null
}

// unsubscribeFromPush — tear down the existing browser subscription and
// return its endpoint so the hook can delete the matching DB row. Returns
// null when there was no active subscription.
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return null

  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return null

  const endpoint = sub.endpoint
  await sub.unsubscribe()
  return endpoint
}
